import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { ReactComponent as EnterIcon } from "../icons/Enter.svg";
import { ReactComponent as ShowIcon } from "../icons/Show.svg";
import styles from "./RoomEntryModal.scss";
import { ExitReason } from "./ExitedRoomScreen";
import { FormattedMessage } from "react-intl";
import { calculateTimeLeft } from "./useCountdownTimer";
import CountdownDisplay from "./CountdownDisplay";
import { useEvent } from "./EventContextProvider";
import {
  enterEventRoom,
  getStartTime,
  formatEventDateRange,
  getEndTime,
  eventHasConcluded
} from "../../utils/events-utils";
import { getRoomEntryAccessState, isRoomHostOrAdmin } from "../../utils/room-entry-access";
import {
  Subtitle1,
  makeStyles,
  Button,
  Spinner,
  mergeClasses,
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  DrawerFooter,
  Divider,
  Text
} from "@fluentui/react-components";
import {
  CalendarArrowRight20Regular,
  CalendarClock20Regular,
  Settings20Filled,
  SignOut20Regular,
  Warning20Filled
} from "@fluentui/react-icons";
import { TicketHorizontalFilled } from "@fluentui/react-icons";
import { ReactComponent as EnterOnAR } from "../icons/EnterOnAR.svg";
import { ReactComponent as EnterOnVR } from "../icons/EnterOnVR.svg";
import configManager from "../../singleton/ConfigManager";
import useBreakpoints from "../hooks/useBreakpoints";
import { useSettingsContext } from "./SettingsContext";
import { useModalContext } from "./ModalContext";
import { TicketDialog } from "./TicketDialog";
import CalendarButtons from "./CalendarButtons";
import configs from "../../utils/configs";

const useStyles = makeStyles({
  button: {
    width: "100%"
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  capacityAndTicketContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  titleContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "start",
    gap: 0,
    marginBottom: "4px"
  },
  leaveButton: {
    "&:not(:hover):not(:active):not([data-fui-focus-visible])": {
      color: "var(--colorNeutralForeground2)",
      border: "1px solid var(--colorNeutralStroke2)"
    }
  },
  actionDivider: {
    margin: "4px 0"
  },
  capacity: {
    color: "var(--Gray-3, #828282)",
    weight: "600"
  },
  registeredButton: {
    pointerEvents: "none",
    color: "var(--colorPaletteGreenForeground3, #1DB954)",
    border: "1px solid var(--colorPaletteGreenForeground3, #1DB954)",
    "&:hover, &:active, &:focus, &:hover:active, &:hover:focus, &:hover:active:focus": {
      color: "var(--colorPaletteGreenForeground3, #1DB954)",
      border: "1px solid var(--colorPaletteGreenForeground3, #1DB954)"
    }
  },
  soldOutButton: {
    pointerEvents: "none",
    color: "var(--colorPaletteRedForeground1)",
    border: "1px solid var(--colorPaletteRedForeground1)",
    "&:hover, &:active, &:focus, &:hover:active, &:hover:focus, &:hover:active:focus": {
      color: "var(--colorPaletteRedForeground1)",
      border: "1px solid var(--colorPaletteRedForeground1)"
    }
  },
  ticketButton: {
    fontSize: "var(--fontSizeBase200, 12px)"
  }
});

export function RoomEntryModal({
  roomId,
  roomName,
  showJoinRoom,
  onJoinRoom,
  onJoinAR,
  showEnterOnDevice,
  onEnterOnDevice,
  showSpectate,
  onSpectate,
  roomUserData,
  canEnter,
  canLookAround,
  onOpenSchedule,
  isJoiningVerse
}) {
  const breakpoint = useBreakpoints();
  const { showNonHistoriedDialog, closeDialog } = useModalContext();

  const {
    eventData,
    error: eventError,
    slug,
    presentationData,
    isPresentation,
    isInTheEvent,
    handleRSVP,
    handleCurrentRoomAdmission,
    hasActiveTicket
  } = useEvent();
  const [isReady, setIsReady] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const { onOpenChange: onOpenSettingsChange } = useSettingsContext();

  const onVerseSettings = () => {
    onOpenSettingsChange(true);
  };

  const classes = useStyles();

  const onBack = () => {
    APP.scene.emit("leave_room_requested", { roomUnavailableReason: ExitReason.left });
  };

  useEffect(() => {
    if (eventData) {
      window.APP.eventData = eventData;

      const roomStartTime = getStartTime(presentationData || eventData);

      const hasStartTimeNotPassed = roomStartTime && roomStartTime > Date.now();

      if (hasStartTimeNotPassed) {
        setStartTime(roomStartTime);
        setTimeLeft(calculateTimeLeft(roomStartTime));
      }

      setIsReady(true);
    }

    if (!slug || eventError) {
      const roomStartTime = roomUserData?.start_time ? new Date(roomUserData.start_time).getTime() : null;
      setStartTime(roomStartTime);
      setTimeLeft(calculateTimeLeft(roomStartTime));
      setIsReady(true);
    }
  }, [slug, eventError, eventData, roomUserData?.start_time, roomId, roomName, presentationData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (timeLeft) {
        setTimeLeft(calculateTimeLeft(startTime));
      } else {
        clearTimeout(timer);
      }
    }, 1000);

    return () => clearTimeout(timer);
  });

  const [open, setIsOpen] = useState(true);

  const callbackOpen = cb => {
    if (isJoiningVerse) return undefined;

    const result = cb && cb();

    if (result && typeof result.then === "function") {
      result.then(
        shouldClose => {
          if (shouldClose !== false) {
            setIsOpen(false);
          }
        },
        () => {}
      );
      return result;
    }

    setIsOpen(false);
    return result;
  };

  const showExitButton = configManager.config.roomEntryDialog.exitButton.enabled;

  const allEntriesDisabled =
    !configManager.config.roomEntryDialog?.roomName?.enabled &&
    !configManager.config.roomEntryDialog?.joinMetaverse?.enabled &&
    !configManager.config.roomEntryDialog?.enterOnAR?.enabled &&
    !configManager.config.roomEntryDialog?.enterOnVR?.enabled &&
    !configManager.config.roomEntryDialog?.lookAround?.enabled &&
    !configManager.config.roomEntryDialog?.roomSettings?.enabled &&
    !configManager.config.roomEntryDialog?.poweredBy?.enabled;

  if (allEntriesDisabled) {
    return <></>;
  }

  // Core state checks
  const isEvent = !!(eventData || presentationData);
  const isRegularRoom = !isEvent;

  // Check if event/presentation has concluded
  const eventEndTime = getEndTime(presentationData || eventData);
  const hasEventConcluded = eventHasConcluded(eventEndTime);
  const hubChannel = typeof window !== "undefined" ? window.APP?.hubChannel : null;
  const userCanBypassEventGate =
    isEvent &&
    isRoomHostOrAdmin({
      hubChannel,
      isAdmin: typeof configs.isAdmin === "function" && configs.isAdmin()
    });

  // User access states
  const { hasEventStarted, userHasRSVPed, userHasNotRSVPed, canAccessEvent, canJoinEventNow } = getRoomEntryAccessState(
    {
      slug,
      isInTheEvent,
      hasEventConcluded,
      timeLeft,
      userCanBypassEventGate
    }
  );
  const canJoinRoomNow = isEvent ? canJoinEventNow : hasEventStarted;

  // Additional states for ticket/sold-out/registered
  const capacity = Number(presentationData?.capacity || eventData?.capacity || 0);
  const attendeesCount = Number(presentationData?.attendees_count || eventData?.attendees_count || 0);
  const isSoldOut = isEvent && Number.isFinite(capacity) && capacity > 0 && attendeesCount >= capacity;
  const isInPerson =
    (presentationData?.type || eventData?.type) !== "virtual" &&
    (presentationData?.address?.id || eventData?.address?.id);
  const checkinUrl = eventData?.current_user?.registration?.check_in_url;
  const showMyTicketButton = userHasRSVPed && isInPerson && !!checkinUrl && !hasEventConcluded;

  const onShowMyTicket = () => {
    const activationLike = {
      name: presentationData?.name || eventData?.name,
      start_time:
        presentationData?.start_time_next ||
        presentationData?.start_time ||
        eventData?.start_time_next ||
        eventData?.start_time,
      end_time:
        presentationData?.end_time_next ||
        presentationData?.end_time ||
        eventData?.end_time_next ||
        eventData?.end_time,
      address: presentationData?.address || eventData?.address,
      checkin_url: checkinUrl,
      slug: presentationData?.slug || eventData?.slug
    };
    showNonHistoriedDialog(TicketDialog, {
      open: true,
      activation: activationLike,
      onClose: () => closeDialog && closeDialog()
    });
  };

  // Button visibility conditions
  const showEventJoinButton =
    isReady &&
    canEnter &&
    isEvent &&
    canAccessEvent &&
    showJoinRoom &&
    configManager.config.roomEntryDialog.joinMetaverse.enabled;

  const showRegularJoinButton =
    isReady &&
    canEnter &&
    isRegularRoom &&
    hasEventStarted &&
    showJoinRoom &&
    configManager.config.roomEntryDialog.joinMetaverse.enabled;

  const showARButton =
    isReady && canEnter && canJoinRoomNow && canAccessEvent && configManager.config.roomEntryDialog.enterOnAR.enabled;

  const showVRButton =
    isReady &&
    canEnter &&
    canJoinRoomNow &&
    canAccessEvent &&
    showEnterOnDevice &&
    configManager.config.roomEntryDialog?.enterOnVR.enabled;

  const showRSVPButton = slug && isReady && !canAccessEvent && !isSoldOut;

  const showRoomFullMessage = showSpectate && canEnter === false;
  const showRoomFullLookAround =
    showRoomFullMessage && canLookAround && configManager.config.roomEntryDialog?.lookAround?.enabled;
  const showLookAroundButton =
    showSpectate &&
    configManager.config.roomEntryDialog?.lookAround?.enabled &&
    (!showRoomFullMessage || canLookAround);
  const showScheduleButton = isEvent && eventData?.presentations?.length > 0;

  const requestEventEntry = action => {
    return enterEventRoom({
      isEvent,
      hasConcluded: hasEventConcluded,
      action,
      open: callbackOpen,
      admit: async () => {
        const admittedToCurrentRoom = await handleCurrentRoomAdmission();
        return admittedToCurrentRoom ? callbackOpen(action) : false;
      }
    });
  };

  const showSettingsButton =
    isReady &&
    breakpoint !== "sm" &&
    configManager.config.roomEntryDialog?.roomSettings?.enabled &&
    (isRegularRoom || userHasRSVPed);

  const joinButtonIcon = isJoiningVerse ? <Spinner size="tiny" /> : <EnterIcon />;
  const joinButtonLabel = isJoiningVerse ? (
    <FormattedMessage id="room-entry-modal.joining-verse-button" defaultMessage="Joining Verse..." />
  ) : null;

  return (
    <OverlayDrawer
      id="room-entry-modal"
      size="small"
      position={["xs", "sm", "md"].includes(breakpoint) ? "bottom" : "end"}
      open={open}
      modalType="non-modal"
      className={mergeClasses(styles.entryCard)}
    >
      <DrawerHeader className={classes.titleContainer}>
        <DrawerHeaderTitle style={{ width: "100%" }}>
          {configManager.config.roomEntryDialog.roomName.enabled && <Subtitle1>{roomName}</Subtitle1>}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody className={classes.dialogContent}>
        {(eventData || presentationData) && (
          <div className={classes.capacityAndTicketContainer}>
            <div className={classes.capacity}>
              <FormattedMessage id="room-entry-modal.capacity" defaultMessage="Capacity" />
              :&nbsp;
              {presentationData?.attendees_count || eventData?.attendees_count || 0}/
              {presentationData?.capacity || eventData?.capacity || 50}
            </div>
            {showMyTicketButton && (
              <Button
                appearance="secondary"
                className={classes.ticketButton}
                icon={<TicketHorizontalFilled />}
                onClick={onShowMyTicket}
                type="button"
              >
                <span>
                  <FormattedMessage id="room-entry-modal.my-ticket" defaultMessage="My Ticket" />
                </span>
              </Button>
            )}
          </div>
        )}

        {/* Show event/presentation dates when available */}
        {!timeLeft && (eventData || presentationData) && (
          <div>
            <Text weight="bold" size={400}>
              {formatEventDateRange(presentationData || eventData)}
            </Text>
          </div>
        )}

        {(presentationData?.description || eventData?.description) && (
          <div className={styles.description}>
            <Text>{presentationData?.description || eventData?.description}</Text>
          </div>
        )}

        {/* Show countdown if there is timeLeft */}
        {timeLeft && (
          <div className={styles.countdownContainer}>
            <CountdownDisplay timeLeft={timeLeft} isPresentation={isPresentation} />
            {(eventData || presentationData) && (
              <CalendarButtons
                googleCalendarUrl={presentationData?.google_calendar_url || eventData?.google_calendar_url}
                outlookCalendarUrl={presentationData?.outlook_calendar_url || eventData?.outlook_calendar_url}
              />
            )}
          </div>
        )}

        {/* Show buttons based on conditions */}
        {showEventJoinButton && (
          <Button
            id="join-verse-button"
            icon={joinButtonIcon}
            className={classes.button}
            appearance="primary"
            disabled={!canJoinEventNow || isJoiningVerse}
            onClick={() => {
              // Safety check to prevent joining if event hasn't started yet
              if (!canJoinEventNow || isJoiningVerse) {
                console.warn("Cannot join: event has not started yet");
                return;
              }
              requestEventEntry(onJoinRoom);
            }}
          >
            <span>
              {joinButtonLabel ||
                (!isRegularRoom && hasEventConcluded ? (
                  isPresentation && presentationData?.entry_kind === "external" ? (
                    <FormattedMessage
                      id="room-entry-modal.visit-presentation-button"
                      defaultMessage="Visit Presentation"
                    />
                  ) : eventData?.entry_kind === "external" ? (
                    <FormattedMessage id="room-entry-modal.visit-activation-button" defaultMessage="Visit Activation" />
                  ) : (
                    <FormattedMessage
                      id="room-entry-modal.explore-past-activation"
                      defaultMessage="Explore Past Activation"
                    />
                  )
                ) : isPresentation ? (
                  <FormattedMessage id="room-entry-modal.join-presentation-button" defaultMessage="Join Presentation" />
                ) : eventData ? (
                  <FormattedMessage id="room-entry-modal.join-event-button" defaultMessage="Join Activation" />
                ) : (
                  <FormattedMessage id="room-entry-modal.join-room-button" defaultMessage="Join Verse" />
                ))}
            </span>
          </Button>
        )}

        {/* Regular room join button - only show when no event/presentation and no countdown */}
        {showRegularJoinButton && (
          <Button
            id="join-verse-button"
            icon={joinButtonIcon}
            className={classes.button}
            appearance="primary"
            disabled={isJoiningVerse}
            onClick={() => callbackOpen(onJoinRoom)}
          >
            <span>
              {joinButtonLabel || (
                <FormattedMessage id="room-entry-modal.join-room-button" defaultMessage="Join Verse" />
              )}
            </span>
          </Button>
        )}

        {/* VR button - only show when no countdown and user is in event */}
        {showVRButton && (
          <Button
            id="enter-vr-button"
            icon={<EnterOnVR />}
            className={classes.button}
            disabled={isJoiningVerse}
            onClick={() => requestEventEntry(onEnterOnDevice)}
          >
            <span>
              <FormattedMessage id="room-entry-modal.enter-on-device-button" defaultMessage="Join in VR" />
            </span>
          </Button>
        )}

        {/* AR button - only show when no countdown and user is in event */}
        {showARButton && (
          <Button
            id="enter-ar-button"
            className={classes.button}
            appearance="secondary"
            icon={<EnterOnAR />}
            disabled={isJoiningVerse}
            onClick={() =>
              requestEventEntry(async () => {
                onJoinAR();
                await new Promise(r => setTimeout(r, 0));
                return onJoinRoom();
              })
            }
          >
            <span>
              <FormattedMessage id="room-entry-modal.enter-on-ar-button" defaultMessage="Join in AR" />
            </span>
          </Button>
        )}

        {showRoomFullMessage && (
          <div className={styles.roomFullPanel} role="status">
            <div className={styles.roomFullIcon}>
              <Warning20Filled />
            </div>
            <div className={styles.roomFullText}>
              <Text className={styles.roomFullTitle} weight="semibold">
                <FormattedMessage
                  id="room-entry-modal.room-full-title"
                  defaultMessage="Active participant limit reached"
                />
              </Text>
              <Text className={styles.roomFullBody}>
                {showRoomFullLookAround ? (
                  <FormattedMessage
                    id="room-entry-modal.room-full"
                    defaultMessage="You can still look around while you wait for a spot."
                  />
                ) : (
                  <FormattedMessage
                    id="room-entry-modal.room-full-no-look-around"
                    defaultMessage="Please try again later when an active participant spot opens."
                  />
                )}
              </Text>
            </div>
          </div>
        )}

        {/* Show RSVP/Register button if user is not in the event - independent of timeLeft */}
        {showRSVPButton && (
          <Button
            id="rsvp-button"
            className={classes.button}
            appearance="primary"
            onClick={() => handleRSVP(presentationData?.slug)}
            icon={hasActiveTicket ? <TicketHorizontalFilled /> : <CalendarArrowRight20Regular />}
          >
            <span>
              {hasActiveTicket ? (
                <FormattedMessage id="room-entry-modal.get-ticket-button" defaultMessage="Get Ticket" />
              ) : !timeLeft ? (
                <FormattedMessage id="room-entry-modal.register-button" defaultMessage="Register" />
              ) : (
                <FormattedMessage id="room-entry-modal.rsvp-button" defaultMessage="RSVP" />
              )}
            </span>
          </Button>
        )}

        {/* Registered indicator */}
        {userHasRSVPed && timeLeft && !hasEventConcluded && (
          <Button appearance="outline" className={classes.registeredButton}>
            <span>
              <FormattedMessage id="room-entry-modal.registered" defaultMessage="Registered" />
            </span>
          </Button>
        )}

        {/* Sold out state for non-registered users */}
        {userHasNotRSVPed && isSoldOut && !hasEventConcluded && (
          <Button appearance="outline" className={`${classes.button} ${classes.soldOutButton}`}>
            <span>
              <FormattedMessage id="room-entry-modal.sold-out" defaultMessage="Sold out" />
            </span>
          </Button>
        )}

        {/* Schedule button - show for events that have presentations */}
        {showScheduleButton && (
          <Button
            id="schedule-button"
            icon={<CalendarClock20Regular />}
            className={classes.button}
            onClick={onOpenSchedule}
          >
            <span>
              <FormattedMessage id="room-entry-modal.schedule-button" defaultMessage="Schedule" />
            </span>
          </Button>
        )}

        {showLookAroundButton && (
          <Button
            id="look-around-button"
            icon={<ShowIcon />}
            className={mergeClasses(classes.button, showRoomFullLookAround && styles.lookAroundButton)}
            appearance={showRoomFullLookAround ? "primary" : "secondary"}
            onClick={onSpectate}
          >
            <span>
              <FormattedMessage id="room-entry-modal.spectate-button" defaultMessage="Look around" />
            </span>
          </Button>
        )}

        {showSettingsButton && (
          <>
            <Button
              id="settings-button"
              icon={<Settings20Filled />}
              className={classes.button}
              onClick={onVerseSettings}
            >
              <span>
                <FormattedMessage id="room-entry-modal.room-settings-button" defaultMessage="Settings" />
              </span>
            </Button>
          </>
        )}

        {showExitButton && (
          <>
            <Divider className={classes.actionDivider} />
            <Button
              id="room-exit-button"
              icon={<SignOut20Regular />}
              className={mergeClasses(classes.button, classes.leaveButton)}
              onClick={onBack}
            >
              <span>
                <FormattedMessage id="room-entry-modal.leave-button" defaultMessage="Leave" />
              </span>
            </Button>
          </>
        )}
      </DrawerBody>
      {configManager.config.roomEntryDialog.poweredBy.enabled && (
        <DrawerFooter>
          <Text style={{ textAlign: "center", width: "100%" }} align="center">
            <FormattedMessage
              id="room-entry-modal.powered-by"
              defaultMessage="Powered by {platform}"
              values={{ platform: "MUD XR Verse" }}
            />
          </Text>
        </DrawerFooter>
      )}
    </OverlayDrawer>
  );
}

RoomEntryModal.propTypes = {
  className: PropTypes.string,
  roomId: PropTypes.string,
  roomName: PropTypes.string.isRequired,
  roomDescription: PropTypes.string,
  showJoinRoom: PropTypes.bool,
  onJoinRoom: PropTypes.func,
  showEnterOnDevice: PropTypes.bool,
  onEnterOnDevice: PropTypes.func,
  showSpectate: PropTypes.bool,
  onSpectate: PropTypes.func,
  roomUserData: PropTypes.object,
  canEnter: PropTypes.bool,
  canLookAround: PropTypes.bool,
  onJoinAR: PropTypes.func,
  onOpenSchedule: PropTypes.func,
  isJoiningVerse: PropTypes.bool
};

RoomEntryModal.defaultProps = {
  showJoinRoom: true,
  showEnterOnDevice: true,
  showSpectate: true,
  roomUserData: {},
  canEnter: false,
  canLookAround: true,
  isJoiningVerse: false
};
